import React, { Fragment, useEffect, useState, Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import axios from "axios";
import Routes from "./Routes";
import store from "./store";
import { pickErrorMessage } from "./pickErrorMessage";
import "../../scss/theme_variable.scss";
import "../../scss/app.scss";
import {
  getBearer,
  getOrganization,
  getAuthUser,
  getAuthProgram,
  getAuthRootProgram,
  getAuthPoints,
  getAuthCart,
  getAuthDomain, isAuthenticated,
  getAuthCurrency,
  getAuthCountry,
} from "./auth";
import { setOrganization } from "@/redux/actions/organizationActions";
import { setAuthUser } from "@/redux/actions/userActions";
import { setSentryUserContext } from "@/sentry/userContext";
import { setStoreProgram } from "@/redux/actions/programActions";
import { setCurrency } from "@/redux/actions/currencyActions";
import { setCountry } from "@/redux/actions/countryActions";
import { setRootProgram } from "@/redux/actions/rootProgramActions";
import { setPointBalance } from "@/redux/actions/balanceActions";
import { setCart } from "@/redux/actions/cartActions";
import { FlashMessage, sendFlashMessage } from "@/shared/components/flash";
import { setDomain } from "@/redux/actions/domainActions";
import { setTemplate } from "@/redux/actions/templateActions"; //from API
import { setThemeAction } from "@/redux/actions/themeActions"; //Local Theme
import useIdleTimeout from "@/shared/useIdleTimeout";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import TemplateButton from "@/shared/components/TemplateButton";
import CloseIcon from "mdi-react/CloseIcon";
import { loadAdSenseScript } from "@/utils/adsense";
import MaintenancePage from "@/components/Maintenance/MaintenancePage";
import { rateLimitStore } from './rateLimitStore';
import RateLimitScreen from './RateLimitScreen';
import { resolveTemplate } from './resolveTemplate';

const DEFAULT_THEME_COLOR = "#64748b";
const DEFAULT_THEME_COLOR_RGB = "100, 116, 139";
const DEFAULT_BUTTON_COLOR = "#ffffff";
const DEFAULT_BUTTON_RADIUS = "10px";

const toRgbString = (color) => {
  if (!color || typeof color !== "string") {
    return DEFAULT_THEME_COLOR_RGB;
  }

  const normalized = color.trim();

  const shortHexMatch = normalized.match(/^#([a-f\d])([a-f\d])([a-f\d])$/i);
  if (shortHexMatch) {
    const [, r, g, b] = shortHexMatch;
    return [
      parseInt(`${r}${r}`, 16),
      parseInt(`${g}${g}`, 16),
      parseInt(`${b}${b}`, 16),
    ].join(", ");
  }

  const hexMatch = normalized.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch) {
    const [, r, g, b] = hexMatch;
    return [
      parseInt(r, 16),
      parseInt(g, 16),
      parseInt(b, 16),
    ].join(", ");
  }

  const rgbMatch = normalized.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return `${r}, ${g}, ${b}`;
  }

  return DEFAULT_THEME_COLOR_RGB;
};

// require('dotenv').config()
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL + "/api/v1";
if (!axios.defaults.headers.common["Authorization"])
  axios.defaults.headers.common["Authorization"] = getBearer();
if (!axios.defaults.headers.post["Content-Type"])
  axios.defaults.headers.post["Content-Type"] = "application/json";

axios.interceptors.request.use(
  (request) => {
    // console.log(request);
    // Edit request config
    return request;
  },
  (error) => {
    // console.log(error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data || {};
    //HANDLE 2FA
    if (
      status === 403 &&
      error.response?.data?.message === "Code is required"
    ) {
      return Promise.reject(error);
    }

    //HANDLE "one login attempt remaining" warning - let LogInForm show its own modal
    if (status === 422 && data?.remaining_attempts === 1) {
      return Promise.reject(error);
    }

    if (status === 429) {
      const { message, retry_after } = error.response.data || {};
      rateLimitStore.set({
        active: true,
        message: message || "Too many requests. Please try again later.",
        retryAfter: retry_after,
      });

      if (retry_after) {
        setTimeout(() => {
          rateLimitStore.set({ active: false, message: "", retryAfter: 0 });
        }, retry_after * 1000);
      }
      console.warn("Rate limit exceeded:", error.response.data);

      if (message) {
        store.dispatch(sendFlashMessage(message, "alert-danger"));
      }

      // Stop further error propagation for 429
      return Promise.resolve({
        data,
        status,
        rateLimited: true,
        handled: true,
      });
    }

    //Handle ALL API errors (400–599)
    if (status >= 400) {
      // Shape-tolerant extraction — never index into a string value (ICOV3-1646).
      const { message, isFieldError } = pickErrorMessage(data);

      const supportUrl = data.support_url;

      // Backend sometimes embeds the support URL directly in the message text
      // (e.g. locked-account errors) rather than only via support_url — linkify it.
      const messageParts = message.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noreferrer">
            {part}
          </a>
        ) : (
          part
        )
      );

      store.dispatch(
        sendFlashMessage(
          <>
            {messageParts} {/* Show support link ONLY for non-field errors */}
            {!isFieldError && supportUrl && (
              <a href={supportUrl} target="_blank" rel="noreferrer">
                here
              </a>
            )}
          </>,
          "alert-danger",
        ),
      );

      return Promise.resolve({
        data,
        status,
        handled: true,
      });
    }

    //Network / unknown errors
    const fallbackMessage = error.request
      ? "Network error. Please check your connection."
      : "Unexpected error occurred.";

    store.dispatch(sendFlashMessage(fallbackMessage, "alert-danger"));

    return Promise.resolve({
      data: null,
      status: 500,
      handled: true,
    });
    // Other errors still reject (500, 401, etc)
    //return Promise.reject(error);
  }
);

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [user, setUser] = useState(false);
  const [program, setProgram] = useState(false);
  const [open, setOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const bannerStatus = useSelector((state) => state.domain?.banner_status?.banner_status);
 const [rateLimit, setRateLimit] = useState({
    active: false,
    message: '',
  });

  useEffect(() => {
    rateLimitStore.subscribe(setRateLimit);
  }, []);
  const timeout = 30 * 60 * 1_000; // 30m
  const promptBeforeIdle = 30 * 1_000 // prompt before 30s

  const onActive = () => {
    setOpen(false)
  }

  const onPrompt = () => {
    setOpen(true)
  }

  const handleStillHere = () =>{
    idleTimer.activate()
  }

  const {idleTimer} = useIdleTimeout({ onActive, onPrompt, timeout, promptBeforeIdle })

  useEffect(() => {
      setIsLoading(false);
      setTimeout(() => setIsLoaded(true), 500);
      setAuthOrganization();
  }, []);

  const setAppTemplate = (domain, program = null) => {
    const template = resolveTemplate(domain, program);
    if ( template ) {
      template.name = template.name.toLowerCase();
      store.dispatch(setTemplate(template));
      setCustomLink(template.font_family, template);
      setThemeCss(template);
      const dirName = template?.name ? template.name : "default";
      const dirUrl = `${process.env.PUBLIC_URL}/theme/${dirName}`;
      store.dispatch(
        setThemeAction({ name: template.name, alias: dirName, dirName, dirUrl })
      );
    }
  }

  const setAppDomain = (domain, program = null) => {
    store.dispatch(setDomain(domain));
    setAppTemplate(domain, program);
    updateDocumentMeta(domain?.program_name);
  }

  useEffect(() => {
    if( user?.id && program?.id ) {
      // console.log("program loaded")
      // console.log(program)
      store.dispatch(setOrganization(getOrganization()));
      const authUser = getAuthUser();
      store.dispatch(setAuthUser(authUser));
      setSentryUserContext(authUser);
      // console.log("authCoutnry",getAuthCountry())
      store.dispatch(setCurrency(getAuthCurrency()));
      store.dispatch(setCountry(getAuthCountry()));
      // store.dispatch(setStoreProgram(getAuthProgram()));
      store.dispatch(setRootProgram(getAuthRootProgram()));
      store.dispatch(setCart(getAuthCart()));
      getAuthPoints().then((balance) => {
        store.dispatch(setPointBalance(balance));
      });
      getAuthDomain().then((domain) => {
        if( domain.program.id !== program.id ) {
          domain.program = program
          setAppDomain(domain, program)
        }
      })
      if (Boolean(program.adsense_adverts)) {
        loadAdSenseScript();
      }
    }
  }, [user, program]);

  const setAuthOrganization = () => {
    getAuthDomain(true).then((domain) => {
      setAppDomain(domain)

      const program = domain?.program;

      const isMaintenance =
        program?.maintenance_mode === 1 ||
        program?.rootProgram?.maintenance_mode === 1;

      setMaintenance(isMaintenance);
      setMaintenanceChecked(true);
    })

    if( !isAuthenticated() )   {
      return;
    }
    const authUser = getAuthUser()
    setSentryUserContext(authUser);

    if( authUser ) {
      idleTimer.start();
      getAuthProgram( true )
      .then( authProgram => {
        if( authProgram ) {
          setUser(authUser)
          setProgram(authProgram)
        }
      })
    }
  };

  const setCustomLink = (href, template) => {
    let head = document.head;
    if (template.name === 'clear') {
      let originalStyle = document.createElement("style");
      originalStyle.innerHTML =
        "*, body, div, p, span, li, ul, i { font-family: 'CircularStdBold', sans-serif;} ";
      head.appendChild(originalStyle);
    }
    if (href) {
      let fullHref =
        "https://fonts.googleapis.com/css?family=" +
        href +
        ":100,300,400,500,700,900";
      let link = document.createElement("link");
      link.type = "text/css";
      link.rel = "stylesheet";
      link.href = fullHref;

      head.appendChild(link);

      let style = document.createElement("style");
      style.innerHTML =
        "*, body, div, p, span, li, ul, i { font-family: '" +
        href +
        "', sans-serif;} ";
      head.appendChild(style);
    }
  };

  const setThemeCss = (template) => {
    const themeColor = template?.theme_color || DEFAULT_THEME_COLOR;
    const buttonBgColor = template?.button_bg_color || themeColor;
    const buttonTextColor = template?.button_color || DEFAULT_BUTTON_COLOR;
    const parsedButtonRadius = Number.parseInt(template?.button_corner, 10);
    const buttonRadius = Number.isFinite(parsedButtonRadius) ? `${parsedButtonRadius}px` : DEFAULT_BUTTON_RADIUS;

    document.documentElement.style.setProperty('--app-themeColor', themeColor);
    document.documentElement.style.setProperty('--app-themeColor-rgb', toRgbString(themeColor));
    document.documentElement.style.setProperty('--app-button-bg', buttonBgColor);
    document.documentElement.style.setProperty('--app-button-color', buttonTextColor);
    document.documentElement.style.setProperty('--app-button-radius', buttonRadius);
  };

  const updateDocumentMeta = (programName) => {
    const title = programName + ' | Rewards, Incentives and Loyalty Platform';
    const description = programName + ' | Rewards, Incentives and Loyalty Platform';
    document.title = programName;
    const metaTags = [
      { selector: 'meta[name="description"]', attr: 'name', name: 'description', content: description },
      { selector: 'meta[property="og:title"]', attr: 'property', name: 'og:title', content: title },
      { selector: 'meta[property="og:description"]', attr: 'property', name: 'og:description', content: description },
      { selector: 'meta[name="twitter:title"]', attr: 'name', name: 'twitter:title', content: title },
      { selector: 'meta[name="twitter:description"]', attr: 'name', name: 'twitter:description', content: description },
    ];
    metaTags.forEach(({ selector, attr, name, content }) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });
  };

  const IdleModal = ({open, setOpen, handleStillHere}) =>{
    const [remaining, setRemaining] = useState(false);

    useEffect(() => {
      const interval = setInterval(() => {
        setRemaining(Math.ceil(idleTimer.getRemainingTime() / 1000))
      }, 1000)
  
      return () => {
        clearInterval(interval)
      }
    })
    
    return(
      <Modal isOpen={open} toggle={() => setOpen(true)}>
      <div className="close cursor-pointer">
        <CloseIcon onClick={handleStillHere} size={30} />
      </div>
      <ModalHeader>
        Are you still here?
      </ModalHeader>
      <ModalBody>
        <p>Logging out in {remaining} seconds</p>
        <div className="d-flex justify-content-end">
          <TemplateButton type="button" onClick={handleStillHere} text='Im still here' />
        </div>
      </ModalBody>
    </Modal> 
    );
  }

  if (!maintenanceChecked) {
    return null; // or loading spinner
  }

  if (maintenance) {
    return <MaintenancePage />;
  }
  if (rateLimit.active) {
    return <RateLimitScreen message={rateLimit.message} retryAfter={rateLimit.retryAfter} />;
  }
  return (
    <Router>
      <Fragment>
        {/* {!isLoaded && (
              <div className={`load${isLoading ? '' : ' loaded'}`}>
                <div className="load__icon-wrap">
                  <svg className="load__icon">
                    <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                  </svg>
                </div>
              </div>
            )} */}
        {bannerStatus && (
          <style>{`body.login-active .site-status-banner { position: fixed !important; top: 0; left: 0; right: 0; }`}</style>
        )}
        {bannerStatus && (
          <div
            className="site-status-banner"
            role="status"
            style={{
              position: "relative", zIndex: 1040, width: "100%",
              padding: "10px 16px", background: "#fff4e5", color: "#663c00",
              fontSize: 14, fontWeight: 500, lineHeight: 1.4, textAlign: "center",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
            }}
            dangerouslySetInnerHTML={{ __html: bannerStatus }}
          />
        )}
        <Routes />
        <FlashMessage />
        <IdleModal open={open} setOpen={setOpen} handleStillHere={handleStillHere} />
      </Fragment>
    </Router>
  );
};

const AppProvider = () => {
  return (
    <Suspense fallback={"loading"}>
      <Provider store={store}>
          <App />
      </Provider>
    </Suspense>
  );
};

export default AppProvider;
